import {
  DOC_BUILDER_SET_LOADING,
  DOC_BUILDER_SET_ERROR,
  DOC_BUILDER_SET_DOCUMENTS,
  DOC_BUILDER_ADD_DOCUMENT,
  DOC_BUILDER_REMOVE_DOCUMENT,
  DOC_BUILDER_SET_CURRENT_DOC,
  DOC_BUILDER_UPDATE_DOCUMENT,
  DOC_BUILDER_BUILD_STATUS,
  DOC_BUILDER_PAGE_BUILT,
  DOC_BUILDER_BUILD_DONE,
  DOC_BUILDER_BUILD_ERROR,
  DOC_BUILDER_BUILD_RESET,
  DOC_BUILDER_UPDATE_PAGE,
  DOC_BUILDER_ADD_PAGE,
  DOC_BUILDER_REMOVE_PAGE,
  DOC_BUILDER_REORDER_PAGES,
  DOC_BUILDER_ADD_SECTION,
  DOC_BUILDER_UPDATE_SECTION,
  DOC_BUILDER_REMOVE_SECTION,
  DOC_BUILDER_MOVE_SECTION,
  DOC_BUILDER_RESET,
  DOC_BUILDER_AI_EDIT_STATUS,
  DOC_BUILDER_AI_EDIT_DONE,
  DOC_BUILDER_AI_EDIT_ERROR,
  DOC_BUILDER_AI_EDIT_RESET,
  DOC_BUILDER_UNDO,
  DOC_BUILDER_REDO,
  DOC_BUILDER_SET_CHAT_LIST,
  DOC_BUILDER_CHAT_UPSERT_LIST_ITEM,
  DOC_BUILDER_SET_CURRENT_CHAT_ID,
  DOC_BUILDER_CHAT_CLEAR_SESSION,
  DOC_BUILDER_PLAN_SET,
  DOC_BUILDER_PLAN_APPEND,
  DOC_BUILDER_PLAN_STATUS,
  DOC_BUILDER_PLAN_BUILD_DONE,
  DOC_BUILDER_PLAN_RESET,
  DOC_BUILDER_CHAT_SET_MESSAGES,
  DOC_BUILDER_CHAT_ADD_MESSAGE,
  DOC_BUILDER_CHAT_UPDATE_MESSAGE,
  DOC_BUILDER_CHAT_RESET,
} from "./DocBuilderTypes";

const initialState = {
  loading: false,
  error: null,
  documents: [],
  // Undo/Redo history
  history: [],
  historyIndex: -1,
  maxHistorySize: 20,
  currentDoc: null,
  buildingStatus: null,
  buildProgress: { pagesBuilt: 0, totalPages: 0 },
  building: false,
  buildError: null,
  aiEdit: { loading: false, status: null, error: null },
  // Chat history
  docChatList: [],
  currentDocChatId: null,
  // Chat messages
  chatMessages: [],
  // Plan mode
  plan: { text: "", status: null, loading: false, buildSections: null },
};

export const docBuilderReducer = (state = initialState, action) => {
  switch (action.type) {
    case DOC_BUILDER_SET_LOADING:
      return {
        ...state,
        loading: action.payload,
        ...(action.payload ? { error: null } : {}),
      };

    case DOC_BUILDER_SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case DOC_BUILDER_SET_DOCUMENTS:
      return { ...state, documents: action.payload ?? [] };

    case DOC_BUILDER_ADD_DOCUMENT: {
      const doc = action.payload;
      if (!doc?._id) return state;
      const exists = state.documents.find((d) => d._id === doc._id);
      if (exists) return state;
      return { ...state, documents: [doc, ...state.documents] };
    }

    case DOC_BUILDER_REMOVE_DOCUMENT: {
      const id = action.payload;
      return {
        ...state,
        documents: state.documents.filter((d) => d._id !== id),
        currentDoc: state.currentDoc?._id === id ? null : state.currentDoc,
      };
    }

    case DOC_BUILDER_SET_CURRENT_DOC:
      return { ...state, currentDoc: action.payload };

    case DOC_BUILDER_UPDATE_DOCUMENT: {
      const { _id, ...rest } = action.payload || {};
      if (!_id) return state;
      const documents = state.documents.map((d) =>
        d._id === _id ? { ...d, ...rest } : d,
      );
      const currentDoc =
        state.currentDoc?._id === _id
          ? { ...state.currentDoc, ...rest }
          : state.currentDoc;
      return { ...state, documents, currentDoc };
    }

    case DOC_BUILDER_BUILD_STATUS:
      return {
        ...state,
        building: true,
        buildingStatus: action.payload?.title ?? null,
        buildProgress: {
          pagesBuilt:
            action.payload?.pagesBuilt ?? state.buildProgress.pagesBuilt,
          totalPages:
            action.payload?.totalPages ?? state.buildProgress.totalPages,
        },
      };

    case DOC_BUILDER_PAGE_BUILT: {
      const { page, pagesBuilt, totalPages, docId } = action.payload || {};
      if (!page) return state;
      const applyPage = (doc) => {
        if (!doc || String(doc._id) !== String(docId)) return doc;
        const existingIdx = (doc.pages || []).findIndex(
          (p) => p.pageIndex === page.pageIndex,
        );
        let pages;
        if (existingIdx >= 0) {
          pages = doc.pages.map((p, i) =>
            i === existingIdx ? { ...p, ...page } : p,
          );
        } else {
          pages = [...(doc.pages || []), page].sort(
            (a, b) => a.pageIndex - b.pageIndex,
          );
        }
        return {
          ...doc,
          pages,
          pagesBuilt: pagesBuilt ?? doc.pagesBuilt,
          totalPages: totalPages ?? doc.totalPages,
        };
      };
      return {
        ...state,
        currentDoc: applyPage(state.currentDoc),
        documents: state.documents.map(applyPage),
        buildProgress: {
          pagesBuilt: pagesBuilt ?? state.buildProgress.pagesBuilt,
          totalPages: totalPages ?? state.buildProgress.totalPages,
        },
      };
    }

    case DOC_BUILDER_BUILD_DONE:
      return {
        ...state,
        building: false,
        buildingStatus: null,
        buildError: null,
      };

    case DOC_BUILDER_BUILD_ERROR:
      return {
        ...state,
        building: false,
        buildingStatus: null,
        buildError:
          action.payload?.error ?? String(action.payload ?? "Build failed"),
      };

    case DOC_BUILDER_BUILD_RESET:
      return {
        ...state,
        building: false,
        buildingStatus: null,
        buildProgress: { pagesBuilt: 0, totalPages: 0 },
        buildError: null,
      };

    case DOC_BUILDER_UPDATE_PAGE: {
      const { docId, pageIndex, changes } = action.payload || {};
      // Only invalidate layout when the structural sections array is replaced;
      // simple style/title changes shouldn't re-trigger an arrange pass.
      const invalidateLayout = changes && changes.sections !== undefined;
      const applyPageUpdate = (doc) => {
        if (!doc || String(doc._id) !== String(docId)) return doc;
        const pages = (doc.pages || []).map((p) =>
          p.pageIndex === pageIndex ? { ...p, ...changes } : p,
        );
        return invalidateLayout
          ? { ...doc, pages, layoutSettled: false }
          : { ...doc, pages };
      };
      return {
        ...state,
        currentDoc: applyPageUpdate(state.currentDoc),
        documents: state.documents.map(applyPageUpdate),
      };
    }

    case DOC_BUILDER_ADD_PAGE: {
      const { docId, page } = action.payload || {};
      const applyAddPage = (doc) => {
        if (!doc || String(doc._id) !== String(docId)) return doc;
        const pages = [...(doc.pages || []), page];
        return { ...doc, pages, layoutSettled: false };
      };
      return {
        ...state,
        currentDoc: applyAddPage(state.currentDoc),
        documents: state.documents.map(applyAddPage),
      };
    }

    case DOC_BUILDER_REMOVE_PAGE: {
      const { docId, pageIndex } = action.payload || {};
      const applyRemovePage = (doc) => {
        if (!doc || String(doc._id) !== String(docId)) return doc;
        const pages = (doc.pages || []).filter(
          (p) => p.pageIndex !== pageIndex,
        );
        return { ...doc, pages, layoutSettled: false };
      };
      const newRemovePageState = {
        ...state,
        currentDoc: applyRemovePage(state.currentDoc),
        documents: state.documents.map(applyRemovePage),
      };
      return saveToHistory(newRemovePageState);
    }

    case DOC_BUILDER_REORDER_PAGES: {
      const { docId, pages } = action.payload || {};
      const apply = (doc) => {
        if (!doc || String(doc._id) !== String(docId)) return doc;
        return { ...doc, pages, layoutSettled: false };
      };
      const newState = {
        ...state,
        currentDoc: apply(state.currentDoc),
        documents: state.documents.map(apply),
      };
      return saveToHistory(newState);
    }

    case DOC_BUILDER_ADD_SECTION: {
      const { docId, pageIndex, section, insertIndex } = action.payload || {};
      const apply = (doc) => {
        if (!doc || String(doc._id) !== String(docId)) return doc;
        // Assign a unique _id to the section if it doesn't have one
        const sectionWithId = section._id
          ? section
          : {
              ...section,
              _id: `sec_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            };
        const pages = (doc.pages || []).map((p) => {
          if (p.pageIndex !== pageIndex) return p;
          const currentSections = [...(p.sections || [])];
          if (
            insertIndex !== null &&
            insertIndex !== undefined &&
            insertIndex >= 0
          ) {
            currentSections.splice(insertIndex, 0, sectionWithId);
          } else {
            currentSections.push(sectionWithId);
          }
          return { ...p, sections: currentSections };
        });
        return { ...doc, pages, layoutSettled: false };
      };
      const newAddState = {
        ...state,
        currentDoc: apply(state.currentDoc),
        documents: state.documents.map(apply),
      };
      return saveToHistory(newAddState);
    }

    case DOC_BUILDER_UPDATE_SECTION: {
      const { docId, pageIndex, sectionIndex, changes } = action.payload || {};
      const apply = (doc) => {
        if (!doc || String(doc._id) !== String(docId)) return doc;
        const pages = (doc.pages || []).map((p) => {
          if (p.pageIndex !== pageIndex) return p;
          const sections = (p.sections || []).map((s, i) =>
            i === sectionIndex ? { ...s, ...changes } : s,
          );
          return { ...p, sections };
        });
        return { ...doc, pages };
      };
      const newUpdateState = {
        ...state,
        currentDoc: apply(state.currentDoc),
        documents: state.documents.map(apply),
      };
      return saveToHistory(newUpdateState);
    }

    case DOC_BUILDER_REMOVE_SECTION: {
      const { docId, pageIndex, sectionIndex } = action.payload || {};
      const apply = (doc) => {
        if (!doc || String(doc._id) !== String(docId)) return doc;
        const pages = (doc.pages || []).map((p) => {
          if (p.pageIndex !== pageIndex) return p;
          const sections = (p.sections || []).filter(
            (_, i) => i !== sectionIndex,
          );
          return { ...p, sections };
        });
        return { ...doc, pages, layoutSettled: false };
      };
      const newRemoveState = {
        ...state,
        currentDoc: apply(state.currentDoc),
        documents: state.documents.map(apply),
      };
      return saveToHistory(newRemoveState);
    }

    case DOC_BUILDER_MOVE_SECTION: {
      const {
        docId,
        sourcePageIndex,
        targetPageIndex,
        sourceSectionIndex,
        targetSectionIndex,
      } = action.payload || {};
      const apply = (doc) => {
        if (!doc || String(doc._id) !== String(docId)) return doc;
        const pages = [...(doc.pages || [])];
        const sourcePage = pages.find((p) => p.pageIndex === sourcePageIndex);
        const targetPage = pages.find((p) => p.pageIndex === targetPageIndex);
        if (!sourcePage || !targetPage) return doc;

        // Get sections arrays (deep copy to prevent reference issues)
        const sourceSections = JSON.parse(
          JSON.stringify(sourcePage.sections || []),
        );

        // Remove from source
        const [movedSection] = sourceSections.splice(sourceSectionIndex, 1);
        if (!movedSection) return doc;

        // For same-page moves, use the same array after removal
        // For cross-page moves, get target sections separately
        let finalSections;
        if (sourcePageIndex === targetPageIndex) {
          // Same page - use modified sourceSections array
          finalSections = sourceSections;
        } else {
          // Cross-page - separate target sections (deep copy)
          const targetSections = JSON.parse(
            JSON.stringify(targetPage.sections || []),
          );
          finalSections = targetSections;
        }

        // Insert at target position
        const insertIndex =
          targetSectionIndex !== undefined
            ? Math.min(targetSectionIndex, finalSections.length)
            : finalSections.length;
        // Insert a deep copy of movedSection
        finalSections.splice(
          insertIndex,
          0,
          JSON.parse(JSON.stringify(movedSection)),
        );

        return {
          ...doc,
          layoutSettled: false,
          pages: pages.map((p) => {
            if (p.pageIndex === sourcePageIndex) {
              return {
                ...p,
                sections:
                  sourcePageIndex === targetPageIndex
                    ? finalSections
                    : JSON.parse(JSON.stringify(sourceSections)),
              };
            }
            if (p.pageIndex === targetPageIndex) {
              return { ...p, sections: finalSections };
            }
            return p;
          }),
        };
      };
      const newMoveState = {
        ...state,
        currentDoc: apply(state.currentDoc),
        documents: state.documents.map(apply),
      };
      return saveToHistory(newMoveState);
    }

    case DOC_BUILDER_RESET:
      return { ...initialState };

    case DOC_BUILDER_AI_EDIT_STATUS:
      return {
        ...state,
        aiEdit: {
          loading: true,
          status: action.payload?.title ?? null,
          error: null,
        },
      };

    case DOC_BUILDER_AI_EDIT_DONE: {
      const {
        docId: aiDocId,
        pageIndex: aiPageIndex,
        sectionIndex: aiSectionIndex,
        updatedSection: aiUpdatedSection,
        updatedPage: aiUpdatedPage,
      } = action.payload || {};

      console.log("[Reducer] AI_EDIT_DONE payload:", {
        aiDocId,
        aiPageIndex,
        aiSectionIndex,
        hasSection: aiUpdatedSection != null,
        hasPage: aiUpdatedPage != null,
        currentDocId: state.currentDoc?._id,
        idMatch: aiDocId && String(state.currentDoc?._id) === String(aiDocId),
      });

      const applyAiEdit = (doc) => {
        if (!doc || (aiDocId && String(doc._id) !== String(aiDocId)))
          return doc;
        if (aiUpdatedSection != null) {
          const pages = (doc.pages || []).map((p) => {
            if (p.pageIndex !== aiPageIndex) return p;
            const sections = (p.sections || []).map((s, i) =>
              i === aiSectionIndex ? { ...s, ...aiUpdatedSection } : s,
            );
            return { ...p, sections };
          });
          return { ...doc, pages };
        }
        if (aiUpdatedPage != null) {
          const pages = (doc.pages || []).map((p) =>
            p.pageIndex === aiPageIndex ? { ...p, ...aiUpdatedPage } : p,
          );
          return { ...doc, pages };
        }
        return doc;
      };

      const hasUpdate = aiUpdatedSection != null || aiUpdatedPage != null;
      const newAiDoneState = {
        ...state,
        aiEdit: { loading: false, status: null, error: null },
        ...(hasUpdate && {
          currentDoc: applyAiEdit(state.currentDoc),
          documents: state.documents.map(applyAiEdit),
        }),
      };
      return hasUpdate ? saveToHistory(newAiDoneState) : newAiDoneState;
    }

    case DOC_BUILDER_AI_EDIT_ERROR:
      return {
        ...state,
        aiEdit: {
          loading: false,
          status: null,
          error: action.payload?.error ?? "Edit failed",
        },
      };

    case DOC_BUILDER_AI_EDIT_RESET:
      return {
        ...state,
        aiEdit: { loading: false, status: null, error: null },
      };

    case DOC_BUILDER_UNDO: {
      if (state.historyIndex <= 0) return state;
      const newIndex = state.historyIndex - 1;
      const previousState = state.history[newIndex];
      return {
        ...state,
        ...previousState,
        history: state.history,
        historyIndex: newIndex,
        // Preserve these flags
        loading: state.loading,
        error: state.error,
        aiEdit: state.aiEdit,
      };
    }

    case DOC_BUILDER_REDO: {
      if (state.historyIndex >= state.history.length - 1) return state;
      const newIndex = state.historyIndex + 1;
      const nextState = state.history[newIndex];
      return {
        ...state,
        ...nextState,
        history: state.history,
        historyIndex: newIndex,
        // Preserve these flags
        loading: state.loading,
        error: state.error,
        aiEdit: state.aiEdit,
      };
    }

    case DOC_BUILDER_SET_CHAT_LIST:
      return {
        ...state,
        docChatList: Array.isArray(action.payload) ? action.payload : [],
      };

    case DOC_BUILDER_CHAT_UPSERT_LIST_ITEM: {
      const item = action.payload;
      if (!item?._id) return state;
      const list = [...(state.docChatList || [])];
      const idx = list.findIndex((c) => c._id === item._id);
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...item };
      } else {
        list.unshift(item);
      }
      list.sort((a, b) => (b.updateTime || 0) - (a.updateTime || 0));
      return { ...state, docChatList: list };
    }

    case DOC_BUILDER_SET_CURRENT_CHAT_ID:
      return { ...state, currentDocChatId: action.payload ?? null };

    case DOC_BUILDER_CHAT_CLEAR_SESSION:
      return { ...state, currentDocChatId: null };

    case DOC_BUILDER_PLAN_SET:
      return {
        ...state,
        plan: {
          ...state.plan,
          text: action.payload ?? "",
          loading: false,
          status: null,
        },
      };

    case DOC_BUILDER_PLAN_APPEND:
      return {
        ...state,
        plan: {
          ...state.plan,
          text: state.plan.text + action.payload,
          loading: true,
          status: null,
        },
      };

    case DOC_BUILDER_PLAN_STATUS:
      return {
        ...state,
        plan: { ...state.plan, status: action.payload, loading: true },
      };

    case DOC_BUILDER_PLAN_BUILD_DONE:
      return {
        ...state,
        plan: {
          ...state.plan,
          loading: false,
          status: null,
          buildSections: action.payload?.sections ?? null,
        },
      };

    case DOC_BUILDER_PLAN_RESET:
      return {
        ...state,
        plan: { text: "", status: null, loading: false, buildSections: null },
      };

    case DOC_BUILDER_CHAT_SET_MESSAGES:
      return {
        ...state,
        chatMessages: Array.isArray(action.payload) ? action.payload : [],
      };

    case DOC_BUILDER_CHAT_ADD_MESSAGE:
      return {
        ...state,
        chatMessages: [...state.chatMessages, action.payload],
      };

    case DOC_BUILDER_CHAT_UPDATE_MESSAGE: {
      const messages = [...state.chatMessages];
      const lastIndex = messages.length - 1;
      if (lastIndex >= 0) {
        messages[lastIndex] = { ...messages[lastIndex], ...action.payload };
      }
      return { ...state, chatMessages: messages };
    }

    case DOC_BUILDER_CHAT_RESET:
      return { ...state, chatMessages: [] };

    default:
      return state;
  }
};

// Helper function to save state to history
function saveToHistory(state) {
  if (!state.currentDoc) return state;

  const historyEntry = {
    currentDoc: JSON.parse(JSON.stringify(state.currentDoc)),
    documents: JSON.parse(JSON.stringify(state.documents)),
  };

  const newHistory = state.history.slice(0, state.historyIndex + 1);
  newHistory.push(historyEntry);

  // Limit history size
  if (newHistory.length > state.maxHistorySize) {
    newHistory.shift();
  }

  const newHistoryIndex = newHistory.length - 1;

  return {
    ...state,
    history: newHistory,
    historyIndex: newHistoryIndex,
  };
}
